import type { SeriesMove, SeriesPoint } from '@trips/shared/balances';
import { type FormatPrefs, formatMonthDay } from '@trips/shared/format';
import { diffDays } from '@trips/shared/time';
import { useEffect, useId, useRef, useState } from 'react';
import s from './Balances.module.css';

/**
 * A balance as a step line: it holds flat until the next logged balance, then moves. Hover,
 * tap, drag or use the arrow keys to pick a day; the screen shows that day's total and what
 * moved. Dots mark redemptions. Hand-drawn SVG like the Stats charts, sized to its box so dots
 * stay round.
 */

export interface ChartDot {
  on: string;
  /** Like "25,000 · Thompson Madrid". */
  label: string;
}

/** A day that can be picked: each change, plus each redemption. */
export interface ReadoutPoint {
  on: string;
  value: number;
  /** Since the point before; null for the first. */
  change: number | null;
  prevOn: string | null;
  moves: SeriesMove[];
  dots: string[];
}

/** The value at the close of a day: the last point on or before it. */
function valueAt(series: SeriesPoint[], day: string): number {
  let v = series[0]?.value ?? 0;
  for (const p of series) {
    if (p.on > day) break;
    v = p.value;
  }
  return v;
}

export function readoutPoints(series: SeriesPoint[], dots: ChartDot[]): ReadoutPoint[] {
  const days = [...new Set([...series.map((p) => p.on), ...dots.map((d) => d.on)])].sort();
  const out: ReadoutPoint[] = [];
  for (const on of days) {
    const value = valueAt(series, on);
    const prev = out.at(-1);
    out.push({
      on,
      value,
      change: prev ? value - prev.value : null,
      prevOn: prev?.on ?? null,
      moves: series.find((p) => p.on === on)?.moves ?? [],
      dots: dots.filter((d) => d.on === on).map((d) => d.label),
    });
  }
  return out;
}

const H = 196;
const TOP = 14;
const BOTTOM = 10;
const INSET = 6;

/** A round step for gridlines: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(span: number, count: number): number {
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const f = raw / mag;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * mag;
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(340);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) =>
      setWidth(Math.max(200, Math.round(e!.contentRect.width))),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

export function BalanceChart({
  series,
  points,
  dots = [],
  format,
  compact,
  prefs,
  label,
  picked,
  pinned,
  onPick,
}: {
  series: SeriesPoint[];
  points: ReadoutPoint[];
  dots?: ChartDot[];
  format: (v: number) => string;
  compact: (v: number) => string;
  prefs: Pick<FormatPrefs, 'dateFormat'>;
  label: string;
  /** The day being read, if any. */
  picked: string | null;
  /** Kept after the pointer leaves (a tap or click), rather than a hover preview. */
  pinned: boolean;
  onPick: (day: string | null, pin: boolean) => void;
}) {
  const id = useId();
  const [wrapRef, W] = useWidth<HTMLDivElement>();
  const from = series[0]?.on ?? '';
  const to = series.at(-1)?.on ?? from;
  const span = Math.max(1, diffDays(from, to));

  const values = series.map((p) => p.value);
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  const pad =
    hi - lo < Math.max(1, Math.abs(hi) * 0.002)
      ? Math.max(Math.abs(hi) * 0.05, 1)
      : (hi - lo) * 0.18;
  lo = Math.max(0, lo - pad);
  hi += pad;
  const step = niceStep(hi - lo, 3);
  const ticks: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi; t += step) ticks.push(t);

  const x = (on: string) => INSET + (diffDays(from, on) / span) * (W - 2 * INSET);
  const y = (v: number) => TOP + (1 - (v - lo) / (hi - lo)) * (H - TOP - BOTTOM);

  let line = '';
  series.forEach((p, i) => {
    line +=
      i === 0
        ? `M${x(p.on).toFixed(1)} ${y(p.value).toFixed(1)}`
        : `H${x(p.on).toFixed(1)}V${y(p.value).toFixed(1)}`;
  });
  if (series.length === 1) line += `H${(W - INSET).toFixed(1)}`;
  const area = `${line}V${H}H${INSET}Z`;
  const last = series.at(-1);

  const nearest = (clientX: number): string | null => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || !points.length) return null;
    const px = clientX - rect.left;
    let best = points[0]!;
    for (const p of points) if (Math.abs(x(p.on) - px) < Math.abs(x(best.on) - px)) best = p;
    return best.on;
  };

  const a = picked ? points.find((p) => p.on === picked) : undefined;
  const ax = a ? x(a.on) : 0;

  return (
    <figure className={s.chart}>
      <div
        ref={wrapRef}
        className={s.plot}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: the arrow keys move the readout
        tabIndex={0}
        role="img"
        aria-label={`${label}: ${series.map((p) => `${formatMonthDay(p.on, prefs)} ${format(p.value)}`).join(', ')}`}
        onPointerDown={(e) => {
          const day = nearest(e.clientX);
          if (pinned && day === picked) onPick(null, false);
          else onPick(day, true);
        }}
        onPointerMove={(e) => {
          if (e.buttons) onPick(nearest(e.clientX), true);
          else if (e.pointerType === 'mouse' && !pinned) onPick(nearest(e.clientX), false);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === 'mouse' && !pinned) onPick(null, false);
        }}
        onKeyDown={(e) => {
          if (!points.length) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const i = points.findIndex((p) => p.on === picked);
            const next =
              i < 0
                ? e.key === 'ArrowRight'
                  ? 0
                  : points.length - 1
                : Math.min(points.length - 1, Math.max(0, i + (e.key === 'ArrowRight' ? 1 : -1)));
            onPick(points[next]!.on, true);
          } else if (e.key === 'Escape') onPick(null, false);
        }}
      >
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className={s.svg} aria-hidden="true">
          <defs>
            <linearGradient id={`${id}fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" className={s.fillTop} />
              <stop offset="1" className={s.fillBottom} />
            </linearGradient>
          </defs>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={0} x2={W} y1={y(t)} y2={y(t)} className={s.grid} />
              <text x={INSET} y={y(t) - 5} className={s.tick}>
                {compact(t)}
              </text>
            </g>
          ))}
          <path d={area} fill={`url(#${id}fill)`} />
          <path d={line} className={s.line} />
          {a && <line x1={ax} x2={ax} y1={0} y2={H} className={s.cross} />}
          {dots.map((d) => (
            <circle
              key={`${d.on}-${d.label}`}
              cx={x(d.on)}
              cy={y(valueAt(series, d.on))}
              r={4.5}
              className={s.dot}
            />
          ))}
          {last && !a && <circle cx={x(last.on)} cy={y(last.value)} r={4.5} className={s.end} />}
          {a && <circle cx={ax} cy={y(a.value)} r={5} className={s.end} />}
        </svg>
      </div>
      <div className={`${s.axis} num`} aria-hidden="true">
        <span>
          {formatMonthDay(from, prefs)}
          {from.slice(0, 4) !== to.slice(0, 4) ? `, ${from.slice(0, 4)}` : ''}
        </span>
        <span>{from === to ? '' : 'Today'}</span>
      </div>
    </figure>
  );
}
